/**
 * CloseMatterDialog — Close Matter / Client Not Proceeding
 * Three-step governed closure workflow for staff.
 *
 * Step 1 — Matter selection (auto-skipped when only one)
 * Step 2 — Closure reason + optional notes
 * Step 3 — Confirmation summary (financial flags, lifecycle, communication preview)
 * Execute
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronLeft,
  Loader2,
  FileX,
  DollarSign,
  User,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CLOSURE_REASONS = [
  { code: "client_not_proceeding",   label: "Client decided not to proceed" },
  { code: "client_postponed",        label: "Client postponed / may return later" },
  { code: "client_unresponsive",     label: "Client unresponsive after follow-up" },
  { code: "other_provider",          label: "Client selected another provider" },
  { code: "firm_declined",           label: "Firm declined the engagement" },
  { code: "discontinued_at_request", label: "Services discontinued at client's request" },
  { code: "other",                   label: "Other — explanation required" },
];

const NOTES_REQUIRED = new Set(["other"]);
const COMM_REVIEW_REQUIRED = new Set(["firm_declined"]);

export function CloseMatterDialog({ open, onOpenChange, clientId, onSuccess }) {
  const [step, setStep] = useState(1);           // 1 | 2 | 3
  const [matters, setMatters] = useState([]);
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setMatters([]);
      setSelectedMatter(null);
      setReason("");
      setNotes("");
      setPreview(null);
      fetchMatters();
    }
  }, [open, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMatters = useCallback(async () => {
    if (!clientId) return;
    setLoadingMatters(true);
    try {
      const { data } = await axios.get(`${API}/api/close-matter/closeable/${clientId}`);
      const items = data.matters || [];
      setMatters(items);
      // Auto-select if there is only one closeable matter
      if (items.length === 1) {
        setSelectedMatter(items[0]);
      }
    } catch {
      toast.error("Could not load client matters");
    } finally {
      setLoadingMatters(false);
    }
  }, [clientId]);

  const fetchPreview = useCallback(async (matterId, matterType) => {
    if (!clientId) return;
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({ matter_type: matterType || "engagement" });
      if (matterId) params.set("matter_id", matterId);
      const { data } = await axios.get(
        `${API}/api/close-matter/preview/${clientId}?${params.toString()}`
      );
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  }, [clientId]);

  // When moving from step 2 → 3, load preview
  const goToStep3 = useCallback(async () => {
    if (!reason) return;
    await fetchPreview(
      selectedMatter?.matter_id || null,
      selectedMatter?.matter_type || "inquiry",
    );
    setStep(3);
  }, [reason, selectedMatter, fetchPreview]);

  const handleExecute = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/close-matter`, {
        client_id:    clientId,
        matter_type:  selectedMatter?.matter_type || "inquiry",
        matter_id:    selectedMatter?.matter_id || null,
        closure_reason: reason,
        notes:        notes.trim() || null,
        skip_communication: false,
      });
      toast.success("Matter closed successfully");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Matter closure failed — please try again"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const noteRequired = NOTES_REQUIRED.has(reason);
  const commReviewRequired = COMM_REVIEW_REQUIRED.has(reason);
  const canProceedStep2 = reason && (!noteRequired || notes.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="close-matter-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <XCircle className="h-5 w-5" />
            Close Matter
          </DialogTitle>
          <DialogDescription>
            Close this matter and record the reason. All routine workflow activities
            will stop automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
          {["Select Matter", "Reason", "Confirm"].map((label, idx) => (
            <React.Fragment key={idx}>
              <span
                className={
                  step === idx + 1
                    ? "font-semibold text-foreground"
                    : step > idx + 1
                    ? "text-green-600"
                    : ""
                }
              >
                {step > idx + 1 ? <CheckCircle className="inline h-3 w-3 mr-0.5" /> : null}
                {label}
              </span>
              {idx < 2 && <ChevronRight className="h-3 w-3" />}
            </React.Fragment>
          ))}
        </div>

        <Separator className="mb-4" />

        {/* ── STEP 1 — Matter selection ─────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-3" data-testid="close-matter-step-select">
            {loadingMatters ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading matters…
              </div>
            ) : matters.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No open matters found for this client.
                <div className="mt-2 text-xs">
                  (Inquiry-only closure is still available below.)
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  data-testid="close-matter-inquiry-btn"
                  onClick={() => {
                    setSelectedMatter({ matter_type: "inquiry", matter_id: null, label: "Inquiry / Lead" });
                    setStep(2);
                  }}
                >
                  Close Inquiry Only
                </Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Select the matter to close:
                </p>
                {matters.map((m) => (
                  <button
                    key={m.matter_id}
                    data-testid={`close-matter-select-${m.matter_id}`}
                    onClick={() => setSelectedMatter(m)}
                    className={`w-full text-left rounded-lg border p-3 text-sm transition-all hover:border-red-400 ${
                      selectedMatter?.matter_id === m.matter_id
                        ? "border-red-500 bg-red-50 dark:bg-red-950/20"
                        : "border-border"
                    }`}
                  >
                    <div className="font-medium">{m.label}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {m.matter_type}
                      </Badge>
                      <span className="text-muted-foreground text-xs">{m.matter_status}</span>
                      {m.is_signed && (
                        <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                          Signed
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
                {matters.length > 1 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Only the selected matter will be closed. Other active matters remain open.
                  </p>
                )}
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!selectedMatter}
                data-testid="close-matter-next-step2"
                onClick={() => setStep(2)}
              >
                Next <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2 — Closure reason ───────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-4" data-testid="close-matter-step-reason">
            {selectedMatter && (
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Closing: </span>
                <span className="font-medium">{selectedMatter.label}</span>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium mb-2 block">Closure reason *</Label>
              <div className="space-y-2">
                {CLOSURE_REASONS.map((r) => (
                  <label
                    key={r.code}
                    data-testid={`close-reason-${r.code}`}
                    className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer text-sm transition-all hover:border-red-400 ${
                      reason === r.code
                        ? "border-red-500 bg-red-50 dark:bg-red-950/20"
                        : "border-border"
                    }`}
                  >
                    <input
                      type="radio"
                      name="closure_reason"
                      value={r.code}
                      checked={reason === r.code}
                      onChange={() => setReason(r.code)}
                      className="accent-red-600"
                    />
                    {r.label}
                    {r.code === "firm_declined" && (
                      <Badge className="ml-auto text-xs bg-amber-100 text-amber-700 border-amber-300">
                        Review required
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-1 block">
                Internal notes {noteRequired ? "*" : "(optional)"}
              </Label>
              <Textarea
                data-testid="close-matter-notes"
                rows={3}
                placeholder={
                  noteRequired
                    ? "Required — explain the specific circumstances…"
                    : "Add any internal notes for audit purposes…"
                }
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="text-sm"
              />
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <ChevronLeft className="h-3 w-3 mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!canProceedStep2}
                  data-testid="close-matter-next-step3"
                  onClick={goToStep3}
                >
                  {loadingPreview ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : null}
                  Review & Confirm <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3 — Confirmation summary ────────────────────────── */}
        {step === 3 && (
          <div className="space-y-4" data-testid="close-matter-step-confirm">
            {loadingPreview ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Calculating impact…
              </div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="space-y-2 text-sm">
                  {/* Matter label */}
                  <div className="flex items-start gap-2 rounded-lg border p-3 bg-muted/40">
                    <FileX className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium">
                        {selectedMatter?.label} will be closed
                      </div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        Reason:{" "}
                        {CLOSURE_REASONS.find((r) => r.code === reason)?.label}
                        {notes && ` — "${notes.slice(0, 60)}${notes.length > 60 ? "…" : ""}"`}
                      </div>
                    </div>
                  </div>

                  {/* Lifecycle */}
                  {preview?.lifecycle_will_change ? (
                    <div className="flex items-start gap-2 rounded-lg border p-3 border-blue-200 bg-blue-50 dark:bg-blue-950/20"
                      data-testid="close-matter-lifecycle-change">
                      <User className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-blue-700 dark:text-blue-300">
                          Client will become Inactive
                        </div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                          No other active matters or financial obligations remain.
                        </div>
                      </div>
                    </div>
                  ) : preview && !preview.lifecycle_will_change ? (
                    <div className="flex items-start gap-2 rounded-lg border p-3"
                      data-testid="close-matter-lifecycle-unchanged">
                      <User className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="text-muted-foreground text-xs">
                        Client remains Active
                        {preview.active_matters_remaining > 0
                          ? ` — ${preview.active_matters_remaining} other active matter(s) remain`
                          : " — outstanding financial obligation(s) require Finance review"}
                      </div>
                    </div>
                  ) : null}

                  {/* Financial flag */}
                  {preview?.requires_finance_review && (
                    <div className="flex items-start gap-2 rounded-lg border p-3 border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                      data-testid="close-matter-finance-flag">
                      <DollarSign className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-amber-700 dark:text-amber-400">
                          Outstanding invoice(s) — Finance review required
                        </div>
                        <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                          {preview.outstanding_invoices?.length} invoice(s) with balance remaining.
                          Operational closure will proceed; invoices are not voided.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Communication */}
                  {commReviewRequired ? (
                    <div className="flex items-start gap-2 rounded-lg border p-3 border-amber-300 bg-amber-50 dark:bg-amber-950/20"
                      data-testid="close-matter-comm-review">
                      <MessageSquare className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-amber-700 dark:text-amber-400">
                          Closing communication requires staff review
                        </div>
                        <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                          Because the firm initiated this closure, the message will be drafted
                          for your review before sending.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 rounded-lg border p-3"
                      data-testid="close-matter-comm-auto">
                      <MessageSquare className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-green-700 dark:text-green-400">
                          Closing message will be sent automatically
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          A warm, professional thank-you message will be delivered through
                          the client portal.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Blockers */}
                  {preview?.open_blockers > 0 && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground rounded border p-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {preview.open_blockers} open advisory blocker(s) will be resolved as
                      "Matter Closed".
                    </div>
                  )}
                </div>

                {/* Signed-engagement warning */}
                {selectedMatter?.is_signed && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm"
                    data-testid="close-matter-signed-warning">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-amber-700 dark:text-amber-400">
                        This engagement has been signed
                      </div>
                      <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                        The executed agreement and its history are preserved. This action
                        does not delete the signed document or void any financial obligations.
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                <ChevronLeft className="h-3 w-3 mr-1" /> Back
              </Button>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={submitting || loadingPreview}
                  data-testid="close-matter-confirm-btn"
                  onClick={handleExecute}
                >
                  {submitting ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <XCircle className="h-3 w-3 mr-1" />
                  )}
                  Close Matter
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
